import styled from '@emotion/styled'
import { asText, RichTextField } from '@prismicio/client'
import { PrismicRichText } from '@prismicio/react'
import { InlineCaption, InlineElementContainer } from '../atoms/Atoms.tsx'
import { LazyImage } from '../atoms/LazyImage.tsx'
import { BREAKPOINT_MOBILE, useIsPortrait } from '../../util/breakpoints'
import {
  ScrollstoryDocumentDataBodyGridSlice,
  ScrollstoryDocumentDataBodyGridSliceItem,
} from '../../../types.generated'

export interface InlineGridProps {
  grid: ScrollstoryDocumentDataBodyGridSlice
}

export default function InlineGrid(props: InlineGridProps) {
  const isPortrait = useIsPortrait()
  const primary = props.grid.primary

  const imageItems = props.grid.items.filter((item) => !!item.image.url)

  const cells = imageItems.map((item, index) => {
    const useLandscapeImage = !isPortrait || !item.image_portrait.url
    const src = useLandscapeImage ? item.image.url : item.image_portrait.url
    const alt = useLandscapeImage
      ? (item.image.alt ?? '')
      : (item.image_portrait.alt ?? '')

    const hasPin = !!asText(item.pin_text)

    return (
      <GridCell key={index}>
        <GridImage src={src ?? ''} alt={alt} />
        {hasPin && (
          <PinsContainer>
            <ImagePin
              isPortrait={isPortrait}
              baseCss={primary.pin_shared_css}
              {...item}
            />
          </PinsContainer>
        )}
      </GridCell>
    )
  })

  const isOversized = primary.grid_type === 'oversized'

  const gridContent = (
    <GridContainer>
      <GridRow isOversized={isOversized}>{cells}</GridRow>
    </GridContainer>
  )

  if (isOversized) {
    return gridContent
  }

  const caption = asText(primary.media_caption)
  const source = asText(primary.media_source)

  return (
    <InlineElementContainer data-cy="inlineGridContainer">
      {gridContent}
      <InlineCaption>
        {caption} <i>{source}</i>
      </InlineCaption>
    </InlineElementContainer>
  )
}

const GridContainer = styled.div`
  position: relative;
  margin-bottom: 0.5em;
`

const GridRow = styled.div<{ isOversized: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${({ isOversized }) => (isOversized ? '30px' : '18px')};

  @media ${BREAKPOINT_MOBILE} {
    flex-direction: column;
    gap: 12px;
  }
`

const GridCell = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  aspect-ratio: 1 / 1;
  overflow: hidden;

  @media ${BREAKPOINT_MOBILE} {
    flex: 0 0 auto;
    width: 100%;
  }
`

const GridImage = styled(LazyImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const PinsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

interface ImagePinProps extends ScrollstoryDocumentDataBodyGridSliceItem {
  isPortrait: boolean
  baseCss: RichTextField
}

function ImagePin(props: ImagePinProps) {
  const specificCss = props.isPortrait
    ? props.pin_css_portrait
    : props.pin_css_landscape

  return (
    <Pin baseCss={props.baseCss} specificCss={specificCss}>
      <PrismicRichText field={props.pin_text} />
    </Pin>
  )
}

const Pin = styled.div<{ baseCss: RichTextField; specificCss: RichTextField }>`
  position: absolute;
  pointer-events: auto;
  ${({ baseCss }) => asText(baseCss) ?? ''}
  ${({ specificCss }) => asText(specificCss) ?? ''}
`
